import type {
  MotionElement,
  MotionScene,
  MotionValidationResult,
} from "./types";
import { parseMotionColor, getMotionProperty } from "./interpolation";
import { parseMotionPath } from "./path";
export const MOTION_LIMITS = {
  bytes: 65536,
  authored: 150,
  drawn: 1000,
  keyframes: 50,
  depth: 12,
  path: 16384,
  captionWords: 600,
} as const;
export const MOTION_TRUSTED_HOSTS = [
  "storage.autoclipper.live",
  "cdn.autoclipper.live",
  "images.pexels.com",
  "videos.pexels.com",
  "images.unsplash.com",
  "plus.unsplash.com",
  "fonts.gstatic.com",
] as const;
export function isTrustedMotionUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      value.length <= 4096 &&
      url.protocol === "https:" &&
      !url.username &&
      !url.password &&
      (!url.port || url.port === "443") &&
      (MOTION_TRUSTED_HOSTS as readonly string[]).includes(url.hostname) &&
      !url.hash
    );
  } catch {
    return false;
  }
}
type ObjectValue = Record<string, unknown>;
const common = [
  "id",
  "type",
  "start",
  "end",
  "speed",
  "loop",
  "opacity",
  "blendMode",
  "x",
  "y",
  "anchor",
  "rotation",
  "rotateX",
  "rotateY",
  "perspective",
  "scale",
  "scaleX",
  "scaleY",
  "skewX",
  "skewY",
  "blur",
  "brightness",
  "saturate",
  "shadow",
  "repeat",
  "keyframes",
  "enter",
  "exit",
  "loops",
  "wiggle",
  "followPath",
];
/** Enter/exit preset types (also offered by the assistant's caption tool). */
export const MOTION_PRESET_TYPES = [
  "fade",
  "slide",
  "pop",
  "zoom",
  "blur",
  "wipe",
  "typewriter",
  "draw",
  "flip",
  "drop",
  "bounce",
  "glitch",
] as const;
const text = [
  "font",
  "fontUrl",
  "size",
  "weight",
  "italic",
  "fill",
  "stroke",
  "strokeWidth",
  "letterSpacing",
  "lineHeight",
  "align",
  "maxWidth",
  "transform",
];
const shapes = ["width", "height", "fill", "stroke", "strokeWidth"];
const specific: Record<string, string[]> = {
  group: ["children", "width", "height", "clip"],
  text: [
    ...text,
    "content",
    "box",
    "split",
    "stagger",
    "typewriter",
    "countUp",
  ],
  rect: [...shapes, "radius"],
  ellipse: shapes,
  polygon: [...shapes, "sides", "innerRadius"],
  path: [
    "d",
    "fill",
    "stroke",
    "strokeWidth",
    "lineCap",
    "lineJoin",
    "trimStart",
    "trimEnd",
    "trimOffset",
  ],
  image: ["src", "fit", "radius", "width", "height"],
  video: ["src", "fit", "muted", "trimStart", "width", "height"],
  icon: ["name", "color", "strokeWidth", "width", "height"],
  particles: [
    "seed",
    "count",
    "rate",
    "shape",
    "spread",
    "direction",
    "gravity",
    "spin",
    "lifetime",
    "colors",
    "sizes",
    "fadeOut",
    "emoji",
    "name",
    "src",
    "width",
    "height",
  ],
  captions: [
    ...text,
    "words",
    "tiers",
    "defaultTier",
    "wordsPerCue",
    "cueEnter",
    "cueExit",
    "wordEnter",
    "activeWord",
    "spokenWord",
  ],
};
const numericBounds: Record<string, [number, number]> = {
  start: [0, 86400],
  end: [0, 86400],
  t: [0, 86400],
  opacity: [0, 1],
  scale: [0, 100],
  scaleX: [-100, 100],
  scaleY: [-100, 100],
  perspective: [1, 20000],
  blur: [0, 200],
  brightness: [0, 10],
  saturate: [0, 10],
  width: [0, 20000],
  height: [0, 20000],
  radius: [0, 10000],
  size: [1, 1000],
  weight: [100, 1000],
  strokeWidth: [0, 200],
  lineHeight: [0.1, 10],
  letterSpacing: [-100, 100],
  maxWidth: [1, 20000],
  innerRadius: [0, 1],
  sides: [3, 64],
  count: [0, 1000],
  rate: [0, 1000],
  spread: [0, 360],
  lifetime: [0.01, 120],
  speed: [0, 10000],
  trimStart: [0, 86400],
  trimEnd: [0, 1],
  trimOffset: [-100, 100],
  duration: [0.001, 86400],
  amount: [0, 10000],
  frequency: [0, 100],
  stiffness: [0.01, 10000],
  damping: [0, 1000],
  mass: [0.01, 1000],
  padding: [0, 1000],
  offset: [0, 1],
  delay: [0, 86400],
  stagger: [0, 120],
  wordsPerCue: [1, 12],
  decimals: [0, 10],
  seed: [-2147483648, 2147483647],
  top: [0, 10000],
  right: [0, 10000],
  bottom: [0, 10000],
  left: [0, 10000],
  angle: [-36000, 36000],
  x: [-100000, 100000],
  y: [-100000, 100000],
  rotation: [-36000, 36000],
  rotateX: [-36000, 36000],
  rotateY: [-36000, 36000],
  skewX: [-89, 89],
  skewY: [-89, 89],
  direction: [-36000, 36000],
  gravity: [-10000, 10000],
  spin: [-36000, 36000],
  from: [-1e12, 1e12],
  to: [-1e12, 1e12],
};
const integers = new Set([
  "sides",
  "count",
  "seed",
  "wordsPerCue",
  "decimals",
  "weight",
]);
const enumValues: Record<string, string[]> = {
  blendMode: [
    "normal",
    "multiply",
    "screen",
    "overlay",
    "darken",
    "lighten",
    "difference",
    "exclusion",
    "plus-lighter",
  ],
  align: ["left", "center", "right"],
  transform: ["none", "uppercase", "lowercase", "capitalize"],
  split: ["none", "line", "word", "char"],
  fit: ["contain", "cover", "fill"],
  lineCap: ["butt", "round", "square"],
  lineJoin: ["miter", "round", "bevel"],
  shape: ["circle", "rect", "emoji", "icon", "image"],
};
const booleanKeys = new Set([
  "loop",
  "italic",
  "muted",
  "fadeOut",
  "autoRotate",
]);
export function sanitizeMotionScene(raw: unknown): MotionValidationResult {
  const issues: MotionValidationResult["issues"] = [];
  const issue = (
    path: string,
    code: string,
    severity: "error" | "warning" = "error",
  ) => {
    issues.push({ path, code, severity });
  };
  const record = (value: unknown, path: string): ObjectValue | null => {
    if (
      !value ||
      typeof value !== "object" ||
      Array.isArray(value) ||
      ![Object.prototype, null].includes(Object.getPrototypeOf(value))
    ) {
      issue(path, "invalid_object");
      return null;
    }
    return value as ObjectValue;
  };
  const keys = (value: ObjectValue, allowed: string[], path: string) => {
    for (const key of Object.keys(value))
      if (!allowed.includes(key))
        issue(`${path}.${key}`, "unknown_key", "warning");
  };
  const number = (
    value: unknown,
    key: string,
    path: string,
    overrideBounds?: [number, number],
  ): number | undefined => {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      issue(path, "invalid_number");
      return undefined;
    }
    const bounds = overrideBounds ?? numericBounds[key] ?? [-100000, 100000];
    let normalized = Math.max(bounds[0], Math.min(bounds[1], value));
    if (integers.has(key)) normalized = Math.round(normalized);
    if (normalized !== value) issue(path, "clamped_number", "warning");
    return normalized;
  };
  const string = (
    value: unknown,
    path: string,
    limit = 4096,
  ): string | undefined => {
    if (
      typeof value !== "string" ||
      Array.from(value).some(
        (char) =>
          char.charCodeAt(0) < 32 && ![9, 10, 13].includes(char.charCodeAt(0)),
      )
    ) {
      issue(path, "invalid_string");
      return undefined;
    }
    if (value.length > limit) {
      issue(path, "string_limit");
      return undefined;
    }
    return value;
  };
  const color = (value: unknown, path: string): string | undefined => {
    if (typeof value !== "string" || !parseMotionColor(value)) {
      issue(path, "invalid_paint");
      return undefined;
    }
    return value;
  };
  const pathData = (value: unknown, path: string): string | undefined => {
    if (typeof value !== "string" || !parseMotionPath(value)) {
      issue(path, "invalid_path");
      return undefined;
    }
    return value;
  };
  const url = (value: unknown, path: string): string | undefined => {
    if (typeof value !== "string" || !isTrustedMotionUrl(value)) {
      issue(path, "unsafe_url");
      return undefined;
    }
    return value;
  };
  const paint = (value: unknown, path: string): unknown => {
    if (typeof value === "string") return color(value, path);
    const obj = record(value, path);
    if (!obj) return undefined;
    keys(obj, ["type", "angle", "stops"], path);
    if (!["linear", "radial"].includes(String(obj.type)))
      issue(`${path}.type`, "invalid_paint");
    if (
      !Array.isArray(obj.stops) ||
      obj.stops.length < 2 ||
      obj.stops.length > 16
    ) {
      issue(`${path}.stops`, "invalid_gradient");
      return undefined;
    }
    const stops = obj.stops.map((s, i) => {
      const stop = record(s, `${path}.stops[${i}]`);
      if (!stop) return null;
      keys(stop, ["offset", "color"], `${path}.stops[${i}]`);
      return {
        offset: number(stop.offset, "offset", `${path}.stops[${i}].offset`),
        color: color(stop.color, `${path}.stops[${i}].color`),
      };
    });
    if (
      stops.some(
        (s, i) => s && i && Number(s.offset) < Number(stops[i - 1]?.offset),
      )
    )
      issue(path, "invalid_gradient");
    return {
      type: obj.type,
      ...(obj.angle !== undefined
        ? { angle: number(obj.angle, "angle", `${path}.angle`) }
        : {}),
      stops,
    };
  };
  const objectNumbers = (
    value: unknown,
    allowed: string[],
    path: string,
    repeatDelta = false,
  ): ObjectValue | undefined => {
    const obj = record(value, path);
    if (!obj) return undefined;
    keys(obj, allowed, path);
    const result: ObjectValue = {};
    for (const key of allowed)
      if (obj[key] !== undefined)
        result[key] =
          key === "color"
            ? color(obj[key], `${path}.${key}`)
            : number(
                obj[key],
                key,
                `${path}.${key}`,
                repeatDelta && key === "scale"
                  ? [-100, 100]
                  : repeatDelta && key === "opacity"
                    ? [-1, 1]
                    : undefined,
              );
    return result;
  };
  const ease = (value: unknown, path: string): unknown => {
    if (typeof value === "string") {
      if (
        [
          "linear",
          "ease-in",
          "ease-out",
          "ease-in-out",
          "bounce",
          "elastic",
          "back",
        ].includes(value) ||
        /^steps\(([1-9]\d{0,2})\)$/.test(value)
      )
        return value;
      issue(path, "invalid_easing");
      return undefined;
    }
    const obj = record(value, path);
    if (!obj) return undefined;
    if (obj.type === "cubic-bezier") {
      keys(obj, ["type", "values"], path);
      if (
        !Array.isArray(obj.values) ||
        obj.values.length !== 4 ||
        obj.values.some((v) => typeof v !== "number" || !Number.isFinite(v)) ||
        [obj.values[0], obj.values[2]].some((v) => v < 0 || v > 1) ||
        obj.values.some((v) => Math.abs(v) > 100)
      ) {
        issue(path, "invalid_easing");
        return undefined;
      }
      return { type: obj.type, values: [...obj.values] };
    }
    if (obj.type === "spring") {
      keys(obj, ["type", "stiffness", "damping", "mass"], path);
      const result: ObjectValue = { type: obj.type };
      for (const key of ["stiffness", "damping", "mass"])
        if (obj[key] !== undefined)
          result[key] = number(obj[key], key, `${path}.${key}`);
      return result;
    }
    if (obj.type === "steps") {
      keys(obj, ["type", "count"], path);
      return {
        type: obj.type,
        count: Math.max(1, number(obj.count, "count", `${path}.count`) ?? 1),
      };
    }
    issue(path, "invalid_easing");
    return undefined;
  };
  const frames = (value: unknown, prop: string, path: string): unknown[] => {
    if (!Array.isArray(value) || !value.length) {
      issue(path, "invalid_keyframes");
      return [];
    }
    if (value.length > MOTION_LIMITS.keyframes) {
      issue(path, "keyframe_limit");
      return [];
    }
    const result: { t?: number; v?: unknown; ease?: unknown }[] = value.map(
      (frame, i) => {
        const framePath = `${path}[${i}]`;
        const obj = record(frame, framePath);
        if (!obj) return {};
        keys(obj, ["t", "v", "ease"], framePath);
        return {
          t: number(obj.t, "t", `${framePath}.t`),
          v:
            prop === "d"
              ? pathData(obj.v, `${framePath}.v`)
              : ["fill", "stroke", "color"].includes(prop.split(".").pop()!)
                ? color(obj.v, `${framePath}.v`)
                : number(obj.v, prop.split(".").pop()!, `${framePath}.v`),
          ...(obj.ease !== undefined
            ? { ease: ease(obj.ease, `${framePath}.ease`) }
            : {}),
        };
      },
    );
    if (result.some((f, i) => i > 0 && (f.t ?? 0) <= (result[i - 1].t ?? 0)))
      issue(path, "invalid_keyframe_order");
    if (prop === "d") {
      const first = parseMotionPath(String(result[0]?.v));
      if (
        result.some((f) => {
          const next = parseMotionPath(String(f.v));
          return (
            !first ||
            !next ||
            first.length !== next.length ||
            first.some((c, i) => c.command !== next[i].command)
          );
        })
      )
        issue(path, "incompatible_path");
    }
    return result;
  };
  const style = (obj: ObjectValue, path: string): ObjectValue => {
    const result: ObjectValue = {};
    for (const key of text)
      if (obj[key] !== undefined) {
        const p = `${path}.${key}`;
        if (key === "fill") result[key] = paint(obj[key], p);
        else if (key === "stroke") result[key] = color(obj[key], p);
        else if (key === "fontUrl") result[key] = url(obj[key], p);
        else if (key === "font") {
          result[key] = string(obj[key], p, 120);
          if (
            !/^[\p{L}\p{N} _.()-]+$/u.test(String(obj[key])) ||
            /url\s*\(/i.test(String(obj[key]))
          )
            issue(p, "invalid_font");
        } else if (booleanKeys.has(key)) {
          if (typeof obj[key] !== "boolean") issue(p, "invalid_boolean");
          else result[key] = obj[key];
        } else if (enumValues[key]) {
          if (!enumValues[key].includes(String(obj[key])))
            issue(p, "invalid_enum");
          else result[key] = obj[key];
        } else result[key] = number(obj[key], key, p);
      }
    if (obj.shadow !== undefined)
      result.shadow = objectNumbers(
        obj.shadow,
        ["color", "blur", "x", "y"],
        `${path}.shadow`,
      );
    return result;
  };
  const preset = (v: unknown, p: string): ObjectValue | undefined => {
    const value = record(v, p);
    if (!value) return undefined;
    keys(value, ["type", "duration", "direction", "stagger"], p);
    if (!(MOTION_PRESET_TYPES as readonly string[]).includes(String(value.type)))
      issue(p, "invalid_preset");
    const normalized: ObjectValue = { type: value.type };
    for (const k of ["duration", "stagger"])
      if (value[k] !== undefined)
        normalized[k] = number(value[k], k, `${p}.${k}`);
    if (value.direction !== undefined) {
      if (!["left", "right", "up", "down"].includes(String(value.direction)))
        issue(p, "invalid_enum");
      normalized.direction = value.direction;
    }
    return normalized;
  };
  // activeWord / spokenWord: what a caption word changes while (or after) it is said.
  const wordStyle = (v: unknown, p: string): ObjectValue | undefined => {
    const value = record(v, p);
    if (!value) return undefined;
    keys(
      value,
      ["fill", "stroke", "strokeWidth", "shadow", "scale", "opacity", "background", "radius", "padding"],
      p,
    );
    const result: ObjectValue = {};
    if (value.fill !== undefined) result.fill = paint(value.fill, `${p}.fill`);
    if (value.stroke !== undefined) result.stroke = color(value.stroke, `${p}.stroke`);
    if (value.background !== undefined)
      result.background = paint(value.background, `${p}.background`);
    for (const k of ["strokeWidth", "scale", "opacity", "radius", "padding"])
      if (value[k] !== undefined) result[k] = number(value[k], k, `${p}.${k}`);
    if (value.shadow !== undefined)
      result.shadow = objectNumbers(value.shadow, ["color", "blur", "x", "y"], `${p}.shadow`);
    return result;
  };
  const captionAnimation = (obj: ObjectValue, path: string): ObjectValue => {
    const result: ObjectValue = {};
    if (obj.wordEnter !== undefined) result.wordEnter = preset(obj.wordEnter, `${path}.wordEnter`);
    for (const k of ["activeWord", "spokenWord"])
      if (obj[k] !== undefined) result[k] = wordStyle(obj[k], `${path}.${k}`);
    return result;
  };
  let authored = 0;
  const ids = new Set<string>();
  const element = (
    value: unknown,
    path: string,
    depth: number,
  ): MotionElement | null => {
    if (++authored > MOTION_LIMITS.authored) {
      issue(path, "authored_limit");
      return null;
    }
    if (depth > MOTION_LIMITS.depth) {
      issue(path, "depth_limit");
      return null;
    }
    const obj = record(value, path);
    if (!obj) return null;
    if (!specific[String(obj.type)]) {
      issue(`${path}.type`, "invalid_type");
      return null;
    }
    keys(obj, [...common, ...specific[String(obj.type)]], path);
    const id = string(obj.id, `${path}.id`, 80);
    if (!id || !/^[\w-]+$/.test(id)) issue(`${path}.id`, "invalid_id");
    else if (ids.has(id)) issue(`${path}.id`, "duplicate_id");
    else ids.add(id);
    const result: ObjectValue = {
      id,
      type: obj.type,
      start: number(obj.start, "start", `${path}.start`),
      end: number(obj.end, "end", `${path}.end`),
    };
    if (!(Number(result.end) > Number(result.start)))
      issue(path, "invalid_timing");
    const duration = Number(result.end) - Number(result.start);
    for (const key of [...common, ...specific[String(obj.type)]]) {
      if (
        ["id", "type", "start", "end", "shadow", ...text].includes(key) ||
        obj[key] === undefined
      )
        continue;
      const p = `${path}.${key}`;
      const v = obj[key];
      if (numericBounds[key]) {
        if (key === "count" && Number(v) > 1000) issue(p, "drawn_limit");
        result[key] = number(v, key, p);
      } else if (booleanKeys.has(key)) {
        if (typeof v !== "boolean") issue(p, "invalid_boolean");
        else result[key] = v;
      } else if (enumValues[key]) {
        if (!enumValues[key].includes(String(v))) issue(p, "invalid_enum");
        else result[key] = v;
      } else if (key === "anchor")
        result[key] = objectNumbers(v, ["x", "y"], p);
      else if (key === "d") result[key] = pathData(v, p);
      else if (key === "src") result[key] = url(v, p);
      else if (key === "color") result[key] = color(v, p);
      else if (["content", "emoji", "name", "defaultTier"].includes(key)) {
        result[key] = string(v, p, key === "content" ? 8192 : 120);
        if (key === "name" && !/^[A-Za-z][A-Za-z0-9]*$/.test(String(v)))
          issue(p, "invalid_icon");
      } else if (key === "children") {
        if (!Array.isArray(v)) issue(p, "invalid_elements");
        else
          result[key] = v
            .slice(0, 151)
            .map((child, i) => element(child, `${p}[${i}]`, depth + 1))
            .filter(Boolean);
        if (Array.isArray(v) && v.length > 150) issue(p, "authored_limit");
      } else if (key === "clip") {
        if (typeof v === "boolean") result[key] = v;
        else {
          const clip = record(v, p);
          if (clip) {
            const normalized =
              objectNumbers(
                Object.fromEntries(
                  Object.entries(clip).filter(([k]) => k !== "shape"),
                ),
                ["top", "right", "bottom", "left"],
                p,
              ) ?? {};
            if (clip.shape !== undefined)
              normalized.shape = string(clip.shape, `${p}.shape`, 80);
            result[key] = normalized;
          }
        }
      } else if (key === "repeat") {
        result[key] = objectNumbers(
          v,
          ["count", "x", "y", "rotation", "scale", "opacity", "delay"],
          p,
          true,
        );
        if (Number((v as ObjectValue)?.count) > 1000) issue(p, "drawn_limit");
        if (
          !(
            (result[key] as ObjectValue)?.count !== undefined &&
            Number((result[key] as ObjectValue)?.count) >= 1
          )
        )
          issue(p, "invalid_repeat");
      } else if (key === "box") {
        const box = record(v, p);
        if (box) {
          keys(box, ["background", "padding", "radius"], p);
          result[key] = {
            ...(box.background !== undefined
              ? { background: paint(box.background, `${p}.background`) }
              : {}),
            ...(box.padding !== undefined
              ? { padding: number(box.padding, "padding", `${p}.padding`) }
              : {}),
            ...(box.radius !== undefined
              ? { radius: number(box.radius, "radius", `${p}.radius`) }
              : {}),
          };
        }
      } else if (key === "typewriter") {
        if (typeof v === "boolean") result[key] = v;
        else result[key] = objectNumbers(v, ["duration"], p);
      } else if (key === "countUp") {
        const count = record(v, p);
        if (count) {
          keys(
            count,
            ["from", "to", "decimals", "prefix", "suffix", "locale"],
            p,
          );
          result[key] = {
            from: number(count.from, "from", `${p}.from`),
            to: number(count.to, "to", `${p}.to`),
            ...(count.decimals !== undefined
              ? {
                  decimals: number(count.decimals, "decimals", `${p}.decimals`),
                }
              : {}),
          };
          for (const k of ["prefix", "suffix", "locale"])
            if (count[k] !== undefined)
              (result[key] as ObjectValue)[k] = string(
                count[k],
                `${p}.${k}`,
                80,
              );
          if (
            count.locale !== undefined &&
            !/^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/.test(String(count.locale))
          )
            issue(`${p}.locale`, "invalid_locale");
          if (count.locale !== undefined) {
            try {
              new Intl.NumberFormat(String(count.locale));
            } catch {
              issue(`${p}.locale`, "invalid_locale");
            }
          }
        }
      } else if (key === "colors" || key === "sizes") {
        if (!Array.isArray(v) || !v.length || v.length > 32)
          issue(p, "invalid_array");
        else
          result[key] = v.map((entry, i) =>
            key === "colors"
              ? color(entry, `${p}[${i}]`)
              : number(entry, "size", `${p}[${i}]`),
          );
      } else if (
        key === "enter" ||
        key === "exit" ||
        key === "cueEnter" ||
        key === "cueExit" ||
        key === "wordEnter"
      ) {
        const normalized = preset(v, p);
        if (normalized) result[key] = normalized;
      } else if (key === "activeWord" || key === "spokenWord") {
        const normalized = wordStyle(v, p);
        if (normalized) result[key] = normalized;
      } else if (key === "loops") {
        if (!Array.isArray(v) || v.length > 8) issue(p, "invalid_loops");
        else
          result[key] = v.map((entry, i) => {
            const loop = record(entry, `${p}[${i}]`);
            if (!loop) return null;
            keys(loop, ["type", "duration", "amount", "seed"], `${p}[${i}]`);
            if (
              ![
                "float",
                "pulse",
                "spin",
                "wiggle",
                "shake",
                "blink",
                "orbit",
              ].includes(String(loop.type))
            )
              issue(p, "invalid_loop");
            const normalized: ObjectValue = { type: loop.type };
            for (const k of ["duration", "amount", "seed"])
              if (loop[k] !== undefined)
                normalized[k] = number(loop[k], k, `${p}[${i}].${k}`);
            return normalized;
          });
      } else if (key === "wiggle") {
        const wiggle = record(v, p);
        if (wiggle) {
          keys(wiggle, ["prop", "amount", "frequency", "seed"], p);
          const normalized: ObjectValue = {
            prop: string(wiggle.prop, `${p}.prop`, 100),
          };
          for (const k of ["amount", "frequency", "seed"])
            normalized[k] = number(wiggle[k], k, `${p}.${k}`);
          result[key] = normalized;
        }
      } else if (key === "followPath") {
        const follow = record(v, p);
        if (follow) {
          keys(follow, ["d", "progress", "autoRotate"], p);
          if (
            follow.autoRotate !== undefined &&
            typeof follow.autoRotate !== "boolean"
          )
            issue(`${p}.autoRotate`, "invalid_boolean");
          result[key] = {
            d: pathData(follow.d, `${p}.d`),
            progress: frames(follow.progress, "opacity", `${p}.progress`),
            ...(follow.autoRotate !== undefined
              ? { autoRotate: follow.autoRotate }
              : {}),
          };
        }
      } else if (key === "words") {
        if (!Array.isArray(v) || v.length > MOTION_LIMITS.captionWords) {
          issue(p, "caption_limit");
          continue;
        }
        result[key] = v.map((entry, i) => {
          const word = record(entry, `${p}[${i}]`);
          if (!word) return null;
          keys(word, ["text", "start", "end", "tier"], `${p}[${i}]`);
          const normalized = {
            text: string(word.text, `${p}[${i}].text`, 120),
            start: number(word.start, "start", `${p}[${i}].start`),
            end: number(word.end, "end", `${p}[${i}].end`),
            ...(word.tier !== undefined
              ? { tier: string(word.tier, `${p}[${i}].tier`, 80) }
              : {}),
          };
          if (
            !(normalized.end! > normalized.start!) ||
            normalized.end! > duration ||
            (i > 0 &&
              normalized.start! < Number((v[i - 1] as ObjectValue)?.start))
          )
            issue(`${p}[${i}]`, "invalid_caption_timing");
          return normalized;
        });
      } else if (key === "tiers") {
        if (!Array.isArray(v) || !v.length || v.length > 6) {
          issue(p, "invalid_tiers");
          continue;
        }
        const tierIds = new Set();
        result[key] = v.map((entry, i) => {
          const tier = record(entry, `${p}[${i}]`);
          if (!tier) return null;
          keys(
            tier,
            ["id", ...text, "shadow", "wordEnter", "activeWord", "spokenWord"],
            `${p}[${i}]`,
          );
          const id = string(tier.id, `${p}[${i}].id`, 80);
          if (!id || tierIds.has(id)) issue(`${p}[${i}].id`, "invalid_tier");
          tierIds.add(id);
          return {
            id,
            ...style(tier, `${p}[${i}]`),
            ...captionAnimation(tier, `${p}[${i}]`),
          };
        });
      }
      // keyframes are validated against the sanitized property tree below.
    }
    Object.assign(
      result,
      style(
        Object.fromEntries(
          Object.entries(obj).filter(([key]) =>
            [...specific[String(obj.type)], "shadow"].includes(key),
          ),
        ),
        path,
      ),
    );
    if (obj.type === "path" && result.trimStart !== undefined)
      result.trimStart = Math.min(1, Number(result.trimStart));
    if (obj.type === "particles" && result.shape === "image" && !result.src)
      issue(`${path}.src`, "missing_src");
    if (obj.type === "particles" && result.shape === "icon" && !result.name)
      issue(`${path}.name`, "invalid_icon");
    if (obj.type === "video") {
      result.muted = true;
      if (result.speed !== undefined && Number(result.speed) <= 0)
        issue(`${path}.speed`, "invalid_video_speed");
    }
    if (["image", "video"].includes(String(obj.type)) && !result.src)
      issue(`${path}.src`, "missing_src");
    if (obj.type === "path" && !result.d) issue(`${path}.d`, "invalid_path");
    if (obj.type === "text" && typeof result.content !== "string")
      issue(`${path}.content`, "missing_content");
    if (obj.type === "group" && !Array.isArray(result.children))
      issue(`${path}.children`, "invalid_elements");
    if (obj.type === "captions") {
      if (!Array.isArray(result.words) || !Array.isArray(result.tiers))
        issue(path, "invalid_captions");
      const tierIds = new Set(
        ((result.tiers as ObjectValue[]) ?? [])
          .filter(Boolean)
          .map((t) => t.id),
      );
      if (
        ((result.words as ObjectValue[]) ?? [])
          .filter(Boolean)
          .some((w) => w.tier !== undefined && !tierIds.has(w.tier)) ||
        (result.defaultTier !== undefined && !tierIds.has(result.defaultTier))
      )
        issue(path, "invalid_tier");
    }
    const allowedAnimated = (prop: string): boolean => {
      if (
        ["__proto__", "constructor", "prototype"].some((k) =>
          prop.split(".").includes(k),
        )
      )
        return false;
      if (
        /^(?:start|end|speed|count|rate|seed|sides|wordsPerCue|repeat|children|words|tiers|src|fontUrl|id|type)(?:\.|$)/.test(
          prop,
        )
      )
        return false;
      const current = getMotionProperty(result, prop);
      return (
        typeof current === "number" ||
        (typeof current === "string" &&
          (Boolean(parseMotionColor(current)) || prop === "d")) ||
        (numericBounds[prop] !== undefined &&
          [...common, ...specific[String(obj.type)]].includes(prop)) ||
        (["fill", "stroke", "color"].includes(prop) &&
          specific[String(obj.type)].includes(prop)) ||
        /^(?:shadow\.(?:x|y|blur|color)|clip\.(?:top|right|bottom|left))$/.test(
          prop,
        )
      );
    };
    if (obj.keyframes !== undefined) {
      const keyframes = record(obj.keyframes, `${path}.keyframes`);
      if (keyframes) {
        result.keyframes = {};
        for (const [prop, value] of Object.entries(keyframes)) {
          if (!allowedAnimated(prop)) {
            issue(`${path}.keyframes.${prop}`, "invalid_animated_property");
            continue;
          }
          (result.keyframes as ObjectValue)[prop] = frames(
            value,
            prop,
            `${path}.keyframes.${prop}`,
          );
        }
      }
    }
    if (
      result.wiggle &&
      (!allowedAnimated(String((result.wiggle as ObjectValue).prop)) ||
        typeof getMotionProperty(
          result,
          String((result.wiggle as ObjectValue).prop),
        ) === "string")
    )
      issue(`${path}.wiggle.prop`, "invalid_animated_property");
    return result as unknown as MotionElement;
  };
  try {
    // Inspect descriptors before serialization: JSON hooks/accessors must never execute.
    const ancestors = new Set<object>();
    let inspected = 0;
    const jsonSafe = (value: unknown, depth: number): boolean => {
      if (++inspected > 100000 || depth > 64) return false;
      if (["function", "symbol", "bigint"].includes(typeof value)) return false;
      if (!value || typeof value !== "object") return true;
      if (ancestors.has(value)) return false;
      if (
        !Array.isArray(value) &&
        ![Object.prototype, null].includes(Object.getPrototypeOf(value))
      )
        return false;
      ancestors.add(value);
      const descriptors = Object.getOwnPropertyDescriptors(value);
      if (Object.getOwnPropertySymbols(value).length) return false;
      for (const descriptor of Object.values(descriptors)) {
        if (
          descriptor.get ||
          descriptor.set ||
          !jsonSafe(descriptor.value, depth + 1)
        )
          return false;
      }
      ancestors.delete(value);
      return true;
    };
    if (!jsonSafe(raw, 0)) {
      issue("$", "invalid_json");
      return { scene: null, issues };
    }
    const serialized = JSON.stringify(raw);
    if (
      !serialized ||
      new TextEncoder().encode(serialized).length > MOTION_LIMITS.bytes
    ) {
      issue("$", "byte_limit");
      return { scene: null, issues };
    }
    const obj = record(raw, "$");
    if (!obj) return { scene: null, issues };
    keys(obj, ["version", "background", "elements"], "$");
    if (obj.version !== 1) issue("$.version", "invalid_version");
    if (!Array.isArray(obj.elements)) {
      issue("$.elements", "invalid_elements");
      return { scene: null, issues };
    }
    if (obj.elements.length > 150) issue("$.elements", "authored_limit");
    const elements = obj.elements
      .slice(0, 151)
      .map((v, i) => element(v, `$.elements[${i}]`, 0))
      .filter(Boolean) as MotionElement[];
    const countDrawn = (el: MotionElement): number => {
      const copies = el.repeat?.count ?? 1;
      let nodes = 1;
      if (el.type === "group")
        nodes += (el.children ?? []).reduce(
          (sum, child) => sum + countDrawn(child),
          0,
        );
      if (el.type === "particles") {
        const lifetime = el.lifetime ?? 2;
        const duration = el.end - el.start;
        nodes +=
          el.count ?? Math.ceil((el.rate ?? 20) * Math.min(lifetime, duration));
      }
      if (el.type === "text") {
        const contents = [el.content ?? ""];
        if (el.countUp) {
          const count = el.countUp;
          for (const value of [count.from, count.to, 0]) {
            let formatted = value.toFixed(count.decimals ?? 0);
            if (count.locale) {
              try {
                formatted = new Intl.NumberFormat(count.locale, {
                  minimumFractionDigits: count.decimals ?? 0,
                  maximumFractionDigits: count.decimals ?? 0,
                }).format(value);
              } catch {
                /* An invalid locale already has an explicit issue. */
              }
            }
            contents.push(
              `${count.prefix ?? ""}${formatted}${count.suffix ?? ""}`,
            );
          }
        }
        if (el.split === "char")
          nodes += Math.max(
            ...contents.map((content) => Array.from(content).length),
          );
        if (el.split === "word")
          nodes += Math.max(
            ...contents.map(
              (content) => content.trim().split(/\s+/).filter(Boolean).length,
            ),
          );
        if (el.split === "line")
          nodes += Math.max(
            ...contents.map((content) => content.split("\n").length),
          );
      }
      if (el.type === "captions") nodes += el.wordsPerCue ?? 6;
      return nodes * copies;
    };
    if (
      elements.reduce((sum, el) => sum + countDrawn(el), 0) >
      MOTION_LIMITS.drawn
    )
      issue("$.elements", "drawn_limit");
    const checkClips = (el: MotionElement, path: string) => {
      if (
        el.type === "group" &&
        el.clip &&
        typeof el.clip === "object" &&
        el.clip.shape
      ) {
        const id = el.clip.shape;
        if (
          !(el.children ?? []).some(
            (child) =>
              child.id === id &&
              ["rect", "ellipse", "polygon", "path"].includes(child.type),
          )
        )
          issue(`${path}.clip.shape`, "invalid_clip");
      }
      (el.children ?? []).forEach((child, i) =>
        checkClips(child, `${path}.children[${i}]`),
      );
    };
    elements.forEach((el, i) => checkClips(el, `$.elements[${i}]`));
    const scene: MotionScene = {
      version: 1,
      ...(obj.background !== undefined
        ? {
            background: paint(
              obj.background,
              "$.background",
            ) as MotionScene["background"],
          }
        : {}),
      elements,
    };
    if (new TextEncoder().encode(JSON.stringify(scene)).length > MOTION_LIMITS.bytes)
      issue('$', 'scene_too_large');
    return {
      scene: issues.some((i) => i.severity === "error") ? null : scene,
      issues,
    };
  } catch {
    issue("$", "invalid_json");
    return { scene: null, issues };
  }
}
